import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import { 
  Phone, PhoneIncoming, PhoneOff, PhoneCall, Users, Clock, Calendar, 
  RefreshCw, Play, Pause, UserCheck, UserX, AlertCircle, CheckCircle,
  Headphones, Radio, Activity, Settings, Plus, ChevronRight, Zap,
  ChevronLeft, Filter, ArrowRight, Crown, Timer
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status Colors and Labels
const STATUS_COLORS = {
  available: 'bg-green-500',
  on_call: 'bg-blue-500',
  wrap_up: 'bg-yellow-500',
  break: 'bg-orange-500',
  unavailable: 'bg-red-500',
  offline: 'bg-gray-500',
};

const STATUS_LABELS = {
  available: 'Available',
  on_call: 'On Call',
  wrap_up: 'Wrap Up',
  break: 'On Break',
  unavailable: 'Unavailable',
  offline: 'Offline',
};

const STATUS_BUTTONS = [
  { status: 'available', label: 'Available', icon: UserCheck, color: 'bg-green-600 hover:bg-green-700' },
  { status: 'break', label: 'Break', icon: Pause, color: 'bg-orange-500 hover:bg-orange-600' },
  { status: 'offline', label: 'Offline', icon: UserX, color: 'bg-gray-500 hover:bg-gray-600' },
];

const SIMULATION_STATES = {
  idle: { label: 'Idle', color: 'bg-gray-400' },
  ringing: { label: 'Ringing', color: 'bg-yellow-500 animate-pulse' },
  connected: { label: 'Connected', color: 'bg-green-500' },
  wrap_up: { label: 'Wrap Up', color: 'bg-orange-500' },
};

// Helper: Format time
const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

// Helper: Format date
const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

// Helper: Generate next 7 days
const getNext7Days = () => {
  const days = [];
  const today = new Date();
  for (let i = 0; i < 8; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    days.push({
      date: d.toISOString().split('T')[0],
      label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : formatDate(d),
      dayName: d.toLocaleDateString('en-US', { weekday: 'short' }),
    });
  }
  return days;
};

// ==========================================
// Rep Card Component
// ==========================================
const RepCard = ({ rep, onStatusChange, isNextInQueue }) => {
  const statusColor = STATUS_COLORS[rep.status] || 'bg-gray-500';
  const simState = SIMULATION_STATES[rep.simulation_state] || SIMULATION_STATES.idle;
  
  const handleStatusClick = (newStatus) => {
    if (rep.status === 'on_call') {
      toast.error('Cannot change status while on call');
      return;
    }
    onStatusChange(rep.user_id, newStatus);
  };
  
  return (
    <Card className={`relative overflow-hidden ${isNextInQueue ? 'ring-2 ring-green-500' : ''}`}>
      {isNextInQueue && (
        <div className="absolute top-0 right-0 bg-green-500 text-white text-xs px-2 py-0.5 rounded-bl flex items-center gap-1">
          <Crown className="h-3 w-3" />
          Next
        </div>
      )}
      
      <CardContent className="p-4">
        {/* Rep Info */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <Headphones className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-background ${statusColor}`} />
            </div>
            <div>
              <h4 className="font-medium text-sm">{rep.user_name}</h4>
              <p className="text-xs text-muted-foreground">{rep.email}</p>
            </div>
          </div>
          <Badge variant="outline" className={`${simState.color} text-white border-0 text-xs`}>
            {simState.label}
          </Badge>
        </div>
        
        {/* Active Call Info */}
        {rep.current_call_id && (
          <div className="bg-blue-500/10 rounded-lg p-2 mb-3 flex items-center gap-2">
            <PhoneCall className="h-4 w-4 text-blue-500 animate-pulse" />
            <span className="text-xs text-blue-400">Active Call</span>
            <span className="text-xs font-mono text-muted-foreground ml-auto">
              {rep.current_call_id.slice(0, 8)}...
            </span>
          </div>
        )}
        
        {/* Stats */}
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
          <span>Calls Today: {rep.calls_handled_today || 0}</span>
        </div>
        
        {/* Quick Status Buttons */}
        <div className="flex gap-1">
          {STATUS_BUTTONS.map(({ status, label, icon: Icon, color }) => (
            <Button
              key={status}
              size="sm"
              variant={rep.status === status ? 'default' : 'outline'}
              className={`flex-1 text-xs h-8 ${rep.status === status ? color + ' text-white' : ''}`}
              onClick={() => handleStatusClick(status)}
              disabled={rep.status === 'on_call'}
            >
              <Icon className="h-3 w-3 mr-1" />
              {label}
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ==========================================
// Slot Card Component
// ==========================================
const SlotCard = ({ slot, onSimulate }) => {
  const isFull = slot.capacity_available === 0;
  const utilization = slot.capacity_total > 0 ? (slot.capacity_used / slot.capacity_total) * 100 : 0;
  
  return (
    <Card className={`${isFull ? 'opacity-60' : ''}`}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium text-sm">
              {formatTime(slot.slot_start)} - {formatTime(slot.slot_end)}
            </span>
          </div>
          {isFull && (
            <Badge variant="destructive" className="text-xs">Full</Badge>
          )}
        </div>
        
        {/* Capacity Bar */}
        <div className="mb-2">
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>Capacity</span>
            <span>{slot.capacity_used}/{slot.capacity_total}</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all ${
                utilization >= 100 ? 'bg-red-500' : 
                utilization >= 75 ? 'bg-yellow-500' : 'bg-green-500'
              }`}
              style={{ width: `${Math.min(utilization, 100)}%` }}
            />
          </div>
        </div>
        
        {/* Stats */}
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">
            {slot.capacity_available} available
          </span>
          {!isFull && onSimulate && (
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-6 text-xs"
              onClick={() => onSimulate(slot)}
            >
              <Zap className="h-3 w-3 mr-1" />
              Test
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ==========================================
// Active Call Card Component
// ==========================================
const ActiveCallCard = ({ call, onComplete }) => {
  const [duration, setDuration] = useState(0);
  
  useEffect(() => {
    if (call.call_started_at) {
      const startTime = new Date(call.call_started_at).getTime();
      const interval = setInterval(() => {
        setDuration(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [call.call_started_at]);
  
  const formatDuration = (secs) => {
    const mins = Math.floor(secs / 60);
    const s = secs % 60;
    return `${mins}:${s.toString().padStart(2, '0')}`;
  };
  
  return (
    <Card className="bg-blue-500/5 border-blue-500/20">
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="font-medium text-sm">{call.customer_name || 'Unknown'}</span>
          </div>
          <Badge variant="outline" className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">
            in_progress
          </Badge>
        </div>
        
        <div className="space-y-1 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <Phone className="h-3 w-3" />
            <span>{call.phone}</span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="h-3 w-3" />
            <span>Rep: {call.assigned_rep_name || 'Unassigned'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Timer className="h-3 w-3" />
            <span>Duration: {formatDuration(duration)}</span>
          </div>
        </div>
        
        {onComplete && (
          <Button 
            size="sm" 
            variant="outline" 
            className="w-full mt-2 h-7 text-xs"
            onClick={() => onComplete(call.id)}
          >
            <CheckCircle className="h-3 w-3 mr-1" />
            Complete Call
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// ==========================================
// Simulate Call Dialog
// ==========================================
const SimulateCallDialog = ({ open, onOpenChange, slots, onSimulate }) => {
  const [selectedSlot, setSelectedSlot] = useState('now');
  const [callCount, setCallCount] = useState('1');
  const [isSimulating, setIsSimulating] = useState(false);
  const [results, setResults] = useState([]);
  
  const handleSimulate = async () => {
    setIsSimulating(true);
    setResults([]);
    
    const count = parseInt(callCount);
    const newResults = [];
    
    for (let i = 0; i < count; i++) {
      try {
        const response = await axios.post(`${API}/discovery/simulate-call`, {
          customer_name: `Test Inbound ${i + 1}`,
          phone: `(555) ${String(Math.floor(Math.random() * 900) + 100)}-${String(Math.floor(Math.random() * 9000) + 1000)}`,
          desired_service: 'Discovery Call Test'
        });
        
        newResults.push({
          success: response.data.success,
          customer: `Test Inbound ${i + 1}`,
          rep: response.data.routing?.assigned_rep_name || 'No rep available',
          message: response.data.message
        });
      } catch (error) {
        newResults.push({
          success: false,
          customer: `Test Inbound ${i + 1}`,
          rep: 'Error',
          message: error.response?.data?.detail || 'Simulation failed'
        });
      }
      
      // Small delay between calls
      if (i < count - 1) {
        await new Promise(r => setTimeout(r, 500));
      }
    }
    
    setResults(newResults);
    setIsSimulating(false);
    onSimulate?.();
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-yellow-500" />
            Simulate Inbound Calls
          </DialogTitle>
          <DialogDescription>
            Test round-robin routing by simulating inbound discovery calls
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Slot Timing</Label>
            <Select value={selectedSlot} onValueChange={setSelectedSlot}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="now">Now (Immediate)</SelectItem>
                {slots?.slice(0, 5).map((slot, i) => (
                  <SelectItem key={i} value={slot.slot_start}>
                    {formatTime(slot.slot_start)} - {formatTime(slot.slot_end)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>Number of Simultaneous Calls</Label>
            <Select value={callCount} onValueChange={setCallCount}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 call</SelectItem>
                <SelectItem value="2">2 calls</SelectItem>
                <SelectItem value="3">3 calls</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Results */}
          {results.length > 0 && (
            <div className="space-y-2">
              <Label>Routing Results</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {results.map((result, i) => (
                  <div 
                    key={i} 
                    className={`p-2 rounded-lg text-sm flex items-center justify-between ${
                      result.success ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                    }`}
                  >
                    <span>{result.customer}</span>
                    <span className="flex items-center gap-1">
                      <ArrowRight className="h-3 w-3" />
                      {result.rep}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={handleSimulate} disabled={isSimulating}>
            {isSimulating ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Simulating...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Run Simulation
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==========================================
// Main Component
// ==========================================
const DiscoveryControlPanel = () => {
  const { user } = useAuth();
  
  // State
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [controlPanelData, setControlPanelData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [slotsData, setSlotsData] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [simulateDialogOpen, setSimulateDialogOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [businessHoursFilter, setBusinessHoursFilter] = useState(true);
  
  const days = useMemo(() => getNext7Days(), []);
  
  // Fetch control panel data
  const fetchControlPanel = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/discovery/control-panel`);
      setControlPanelData(response.data);
    } catch (error) {
      console.error('Error fetching control panel:', error);
      // Don't toast on polling errors
      if (!autoRefresh) {
        toast.error('Failed to load control panel data');
      }
    } finally {
      setLoading(false);
    }
  }, [autoRefresh]);
  
  // Fetch slots for selected date
  const fetchSlots = useCallback(async (date) => {
    setLoadingSlots(true);
    try {
      const response = await axios.get(`${API}/discovery/available-slots`, {
        params: { date }
      });
      setSlotsData(response.data);
    } catch (error) {
      console.error('Error fetching slots:', error);
    } finally {
      setLoadingSlots(false);
    }
  }, []);
  
  // Initial load
  useEffect(() => {
    fetchControlPanel();
    fetchSlots(selectedDate);
  }, []);
  
  // Auto-refresh polling
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchControlPanel();
      fetchSlots(selectedDate);
    }, 5000); // 5 second polling
    
    return () => clearInterval(interval);
  }, [autoRefresh, selectedDate, fetchControlPanel, fetchSlots]);
  
  // Handle date change
  const handleDateChange = (date) => {
    setSelectedDate(date);
    fetchSlots(date);
  };
  
  // Handle rep status change
  const handleRepStatusChange = async (repId, newStatus) => {
    try {
      await axios.post(`${API}/discovery/toggle-rep-availability`, {
        rep_user_id: repId,
        status: newStatus
      });
      toast.success(`Rep status updated to ${STATUS_LABELS[newStatus]}`);
      fetchControlPanel();
    } catch (error) {
      console.error('Error updating rep status:', error);
      toast.error('Failed to update rep status');
    }
  };
  
  // Handle call completion
  const handleCompleteCall = async (bookingId) => {
    try {
      await axios.post(`${API}/discovery/end-call/${bookingId}`, null, {
        params: { outcome: 'Completed via Control Panel' }
      });
      toast.success('Call marked as complete');
      fetchControlPanel();
    } catch (error) {
      console.error('Error completing call:', error);
      toast.error('Failed to complete call');
    }
  };
  
  // Seed data
  const handleSeedData = async () => {
    try {
      const response = await axios.post(`${API}/discovery/seed`);
      toast.success(`Seeded ${response.data.users_created} test users`);
      fetchControlPanel();
    } catch (error) {
      console.error('Error seeding data:', error);
      toast.error('Failed to seed data');
    }
  };
  
  // Calculate next rep in queue (round-robin preview)
  const nextRepInQueue = useMemo(() => {
    if (!controlPanelData?.reps) return null;
    
    const availableReps = controlPanelData.reps
      .filter(r => r.status === 'available' && !r.current_call_id)
      .sort((a, b) => {
        // Sort by calls_handled_today (ascending), then by last_call_ended_at
        const callsA = a.calls_handled_today || 0;
        const callsB = b.calls_handled_today || 0;
        if (callsA !== callsB) return callsA - callsB;
        return (a.last_call_ended_at || '') < (b.last_call_ended_at || '') ? -1 : 1;
      });
    
    return availableReps[0]?.user_id || null;
  }, [controlPanelData?.reps]);
  
  // Filter slots by business hours
  const filteredSlots = useMemo(() => {
    if (!slotsData?.slots) return [];
    if (!businessHoursFilter) return slotsData.slots;
    
    // Show slots within business hours (slots API already filters this)
    return slotsData.slots;
  }, [slotsData?.slots, businessHoursFilter]);
  
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }
  
  const stats = controlPanelData || {
    total_reps: 0,
    available_reps: 0,
    on_call_reps: 0,
    booked_slots_today: 0,
    completed_calls_today: 0,
    reps: [],
    active_calls: []
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Radio className="h-6 w-6 text-primary" />
              Control Panel
            </h1>
            <p className="text-muted-foreground text-sm">
              Manage Discovery Call capacity and routing in real-time
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Auto-refresh Toggle */}
            <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-1.5">
              <Switch 
                checked={autoRefresh} 
                onCheckedChange={setAutoRefresh}
                id="auto-refresh"
              />
              <Label htmlFor="auto-refresh" className="text-sm cursor-pointer">
                Auto-refresh
              </Label>
              {autoRefresh && (
                <RefreshCw className="h-3 w-3 text-green-500 animate-spin" />
              )}
            </div>
            
            {/* Actions */}
            <Button variant="outline" onClick={() => setSimulateDialogOpen(true)}>
              <Zap className="h-4 w-4 mr-2" />
              Simulate Call
            </Button>
            
            <Button variant="outline" onClick={() => setSettingsOpen(true)}>
              <Settings className="h-4 w-4" />
            </Button>
            
            <Button variant="ghost" size="icon" onClick={fetchControlPanel}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Reps</p>
                  <p className="text-2xl font-bold">{stats.total_reps}</p>
                </div>
                <Users className="h-8 w-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-green-500/5 border-green-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-400">Available</p>
                  <p className="text-2xl font-bold text-green-500">{stats.available_reps}</p>
                </div>
                <UserCheck className="h-8 w-8 text-green-500/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-blue-500/5 border-blue-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-400">On Call</p>
                  <p className="text-2xl font-bold text-blue-500">{stats.on_call_reps}</p>
                </div>
                <PhoneCall className="h-8 w-8 text-blue-500/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Booked Today</p>
                  <p className="text-2xl font-bold">{stats.booked_slots_today}</p>
                </div>
                <Calendar className="h-8 w-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                  <p className="text-2xl font-bold text-green-500">{stats.completed_calls_today}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500/50" />
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Main Content */}
        <div className="grid grid-cols-12 gap-6">
          {/* Left Column - Reps & Active Calls */}
          <div className="col-span-4 space-y-6">
            {/* Discovery Agents */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Headphones className="h-5 w-5" />
                      Discovery Agents
                    </CardTitle>
                    <CardDescription>
                      Quick toggle availability and view call states
                    </CardDescription>
                  </div>
                  <Badge variant="outline">
                    {stats.available_reps}/{stats.total_reps} ready
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {stats.reps?.length > 0 ? (
                  stats.reps.map((rep) => (
                    <RepCard 
                      key={rep.user_id} 
                      rep={rep} 
                      onStatusChange={handleRepStatusChange}
                      isNextInQueue={rep.user_id === nextRepInQueue}
                    />
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No discovery agents found</p>
                    <Button 
                      variant="link" 
                      className="mt-2"
                      onClick={handleSeedData}
                    >
                      Seed test agents
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Active Calls */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Activity className="h-5 w-5 text-green-500" />
                    Active Calls
                  </CardTitle>
                  <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/30">
                    {stats.active_calls?.length || 0} live
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {stats.active_calls?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.active_calls.map((call) => (
                      <ActiveCallCard 
                        key={call.id} 
                        call={call}
                        onComplete={handleCompleteCall}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Phone className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No active calls</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Right Column - Slots Calendar */}
          <div className="col-span-8">
            <Card className="h-full">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Time Slots
                    </CardTitle>
                    <CardDescription>
                      Today + next 7 days • Business hours
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={businessHoursFilter} 
                        onCheckedChange={setBusinessHoursFilter}
                        id="bh-filter"
                      />
                      <Label htmlFor="bh-filter" className="text-sm">
                        Business hours only
                      </Label>
                    </div>
                  </div>
                </div>
                
                {/* Date Tabs */}
                <div className="flex gap-1 mt-4 overflow-x-auto pb-1">
                  {days.map((day) => (
                    <Button
                      key={day.date}
                      variant={selectedDate === day.date ? 'default' : 'outline'}
                      size="sm"
                      className="flex-shrink-0"
                      onClick={() => handleDateChange(day.date)}
                    >
                      {day.label}
                    </Button>
                  ))}
                </div>
              </CardHeader>
              
              <CardContent>
                {loadingSlots ? (
                  <div className="flex items-center justify-center h-64">
                    <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredSlots.length > 0 ? (
                  <div className="grid grid-cols-3 gap-3 max-h-[500px] overflow-y-auto pr-2">
                    {filteredSlots.map((slot, i) => (
                      <SlotCard 
                        key={i} 
                        slot={slot}
                        onSimulate={() => setSimulateDialogOpen(true)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 text-muted-foreground">
                    <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No available slots for this date</p>
                    <p className="text-xs mt-1">
                      {selectedDate === new Date().toISOString().split('T')[0] 
                        ? 'Slots may have passed or cutoff time reached'
                        : 'Check business hours configuration'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Simulate Call Dialog */}
        <SimulateCallDialog
          open={simulateDialogOpen}
          onOpenChange={setSimulateDialogOpen}
          slots={filteredSlots}
          onSimulate={fetchControlPanel}
        />
        
        {/* Settings Dialog */}
        <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Discovery Pool Settings</DialogTitle>
              <DialogDescription>
                Configure the Discovery Call pool parameters
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Pool Name</Label>
                <Input 
                  value={controlPanelData?.pool?.name || 'Inbound Discovery Calls'} 
                  disabled 
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Slot Duration</Label>
                  <Input 
                    value={`${controlPanelData?.pool?.slot_duration_minutes || 30} minutes`} 
                    disabled 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Booking Cutoff</Label>
                  <Input 
                    value={`${controlPanelData?.pool?.booking_cutoff_minutes || 30} minutes before`} 
                    disabled 
                  />
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label>Actions</Label>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleSeedData}>
                    <Plus className="h-4 w-4 mr-2" />
                    Seed Test Data
                  </Button>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setSettingsOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DiscoveryControlPanel;
